
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { StrategyFormData, Strategy } from './types';

/**
 * A greatly simplified version of the strategy wizard without animations
 * or complex state transitions to avoid React infinite update loops.
 */
interface SimpleStrategyWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  initialData?: Partial<Strategy> | null;
}

export const SimpleStrategyWizard: React.FC<SimpleStrategyWizardProps> = ({
  open,
  onOpenChange,
  onSubmit,
  initialData = null,
}) => {
  const { toast } = useToast();
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(initialData?.name || '');
      setDescription(initialData?.description || '');
    }
  }, [open, initialData]);

  // Simple form submit handler
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Validation Error",
        description: "Strategy name is required",
        variant: "destructive",
      });
      return;
    }
    
    const data = {
      id: initialData?.id || Date.now().toString(),
      name,
      description,
      // Simple defaults for other required fields
      engine: 'default',
      status: 'active',
      symbols: initialData?.symbols || [],
      riskLevel: initialData?.riskLevel || 'medium',
    };
    
    onSubmit(data);
    
    toast({
      title: initialData ? "Strategy Updated" : "Strategy Created",
      description: `Strategy "${name}" has been saved.`,
    });
    
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{initialData ? 'Edit Strategy' : 'Create New Strategy'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Strategy Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Enter strategy name"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Describe your strategy"
              rows={3}
            />
          </div>
          
          <div className="pt-4 text-sm text-gray-500">
            <p>This is a simplified strategy form. For advanced options, please use the full strategy editor.</p>
          </div>
        </form>
        
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit}>
            {initialData ? 'Update Strategy' : 'Create Strategy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
